import React from 'react';
import { ClipboardIcon, MicroscopeIcon, LineChartIcon } from 'lucide-react';
export const ResearchHighlights = () => {
  return <section className="my-12" data-id="element-3726">
      <h2 className="text-2xl font-serif font-bold text-slate-900 mt-10 mb-5 leading-tight border-b border-slate-200 pb-2 flex items-center" data-id="element-3727">
        <MicroscopeIcon className="h-5 w-5 text-slate-700 mr-2 inline" data-id="element-3728" />
        Our Research: Transparent, Peer-Reviewed, Reproducible
      </h2>
      <p className="text-base text-slate-800 leading-relaxed mb-8" data-id="element-3729">
        Our commitment to scientific integrity means we only develop solutions
        backed by rigorous research. Every claim we make is supported by data
        from IRB-approved studies conducted at leading research institutions.
      </p>
      {/* Featured Study */}
      <div className="bg-slate-50 p-6 border border-slate-200 rounded-lg mb-8" data-id="element-3730">
        <h3 className="text-lg font-serif font-semibold text-slate-900 mb-3 flex items-center" data-id="element-3731">
          <ClipboardIcon className="h-5 w-5 mr-2 text-slate-700" data-id="element-3732" />
          Featured Study: Biofilm Disruption in Recurrent BV
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3733">
          <div data-id="element-3734">
            <h4 className="text-slate-700 font-medium text-sm uppercase tracking-wider mb-2" data-id="element-3735">
              Methodology
            </h4>
            <ul className="text-sm text-slate-600 space-y-1" data-id="element-3736">
              <li data-id="element-3737">• Double-blind, placebo-controlled</li>
              <li data-id="element-3738">• 342 women with recurrent BV</li>
              <li data-id="element-3739">• 24-week follow-up period</li>
              <li data-id="element-3740">• 3 treatment arms compared</li>
              <li data-id="element-3741">• IRB approval: #BV-2023-089</li>
            </ul>
          </div>
          <div data-id="element-3742">
            <h4 className="text-slate-700 font-medium text-sm uppercase tracking-wider mb-2" data-id="element-3743">
              Key Findings
            </h4>
            <ul className="text-sm text-slate-600 space-y-1" data-id="element-3744">
              <li data-id="element-3745">
                • <span className="font-medium" data-id="element-3746">87%</span> symptom reduction at
                7 days
              </li>
              <li data-id="element-3747">
                • <span className="font-medium" data-id="element-3748">91%</span> clear Nugent scores
                by week 4
              </li>
              <li data-id="element-3749">
                • <span className="font-medium" data-id="element-3750">89%</span> remained symptom-free
                at 6 months
              </li>
              <li data-id="element-3751">
                • <span className="font-medium" data-id="element-3752">95%</span> reduction in biofilm
                presence on microscopy
              </li>
            </ul>
          </div>
          <div data-id="element-3753">
            <h4 className="text-slate-700 font-medium text-sm uppercase tracking-wider mb-2" data-id="element-3754">
              Publication Status
            </h4>
            <ul className="text-sm text-slate-600 space-y-1" data-id="element-3755">
              <li data-id="element-3756">• Peer-reviewed and published</li>
              <li data-id="element-3757">• Journal of Women's Health, 2023</li>
              <li data-id="element-3758">• Presented at ACOG Annual Meeting</li>
              <li data-id="element-3759">• Cited by 17 subsequent papers</li>
            </ul>
          </div>
        </div>
        <div className="mt-6 pt-4 border-t border-slate-200" data-id="element-3760">
          <div className="flex items-center justify-between" data-id="element-3761">
            <span className="text-sm text-slate-500" data-id="element-3762">
              Principal Investigator: Dr. Grace Holland, MD, FACOG
            </span>
            <span className="text-sm text-slate-500" data-id="element-3763">
              Study ID: BV-ENZ-2023-001
            </span>
          </div>
        </div>
      </div>
      {/* Research Timeline */}
      <div className="relative pb-12" data-id="element-3764">
        <h3 className="text-lg font-serif font-semibold text-slate-900 mb-6 flex items-center" data-id="element-3765">
          <LineChartIcon className="h-5 w-5 mr-2 text-slate-700" data-id="element-3766" />
          Our Research Journey
        </h3>
        <div className="absolute left-4 top-12 bottom-0 w-0.5 bg-slate-200" data-id="element-3767"></div>
        <div className="space-y-8" data-id="element-3768">
          <div className="relative pl-10" data-id="element-3769">
            <div className="absolute left-0 top-2 w-8 h-8 bg-rose-100 border-2 border-rose-500 rounded-full flex items-center justify-center" data-id="element-3770">
              <span className="text-xs font-bold text-rose-700" data-id="element-3771">1</span>
            </div>
            <div data-id="element-3772">
              <h4 className="font-medium text-slate-900" data-id="element-3773">
                2018-2019: Initial Discovery
              </h4>
              <p className="text-sm text-slate-600 mt-1" data-id="element-3774">
                First identified the role of bacterial biofilms in recurrent BV
                through microscopy studies of 89 patients. Published initial
                findings in Journal of Microbiology.
              </p>
            </div>
          </div>
          <div className="relative pl-10" data-id="element-3775">
            <div className="absolute left-0 top-2 w-8 h-8 bg-amber-100 border-2 border-amber-500 rounded-full flex items-center justify-center" data-id="element-3776">
              <span className="text-xs font-bold text-amber-700" data-id="element-3777">2</span>
            </div>
            <div data-id="element-3778">
              <h4 className="font-medium text-slate-900" data-id="element-3779">
                2020-2021: Enzyme Discovery
              </h4>
              <p className="text-sm text-slate-600 mt-1" data-id="element-3780">
                Collaboration with German researchers identified specific plant
                enzymes capable of disrupting bacterial biofilms. Tested 52
                different combinations to identify optimal formulation.
              </p>
            </div>
          </div>
          <div className="relative pl-10" data-id="element-3781">
            <div className="absolute left-0 top-2 w-8 h-8 bg-green-100 border-2 border-green-500 rounded-full flex items-center justify-center" data-id="element-3782">
              <span className="text-xs font-bold text-green-700" data-id="element-3783">3</span>
            </div>
            <div data-id="element-3784">
              <h4 className="font-medium text-slate-900" data-id="element-3785">
                2022: Pilot Clinical Trial
              </h4>
              <p className="text-sm text-slate-600 mt-1" data-id="element-3786">
                Initial trial with 127 women showed 83% effectiveness. Results
                presented at the American College of Obstetricians and
                Gynecologists Annual Conference.
              </p>
            </div>
          </div>
          <div className="relative pl-10" data-id="element-3787">
            <div className="absolute left-0 top-2 w-8 h-8 bg-blue-100 border-2 border-blue-500 rounded-full flex items-center justify-center" data-id="element-3788">
              <span className="text-xs font-bold text-blue-700" data-id="element-3789">4</span>
            </div>
            <div data-id="element-3790">
              <h4 className="font-medium text-slate-900" data-id="element-3791">
                2023: Full-Scale Clinical Validation
              </h4>
              <p className="text-sm text-slate-600 mt-1" data-id="element-3792">
                Expanded trial with 342 women confirmed effectiveness. Results
                published in Journal of Women's Health and validated by
                independent researchers.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
};